import { Injectable } from '@angular/core';

import * as CryptoJS from 'crypto-js';
import { Encoding, EncodingService } from './encoding.service';

type EncryptionKey = CryptoJS.lib.WordArray;
type CipherHelper = typeof CryptoJS.AES;

export enum CipherAlgorithm {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  AES,
}

export enum CipherMode {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  ECB,
}

export enum CipherPadding {
  ZeroPadding,
}

export interface CreateOptions {
  /**
   * The algorithm to use
   */
  algorithm: CipherAlgorithm;
  /**
   * The default options used for encryption and decryption
   */
  defaultOptions: CipherOptions;
}

export interface CipherOptions {
  mode?: CipherMode;
  padding?: CipherPadding;
  secret: EncryptionKey;
}

/**
 * Maps the mode enum to the corresponding CryptoJS mode.
 *
 * @param mode The mode to map
 */
function getCryptoJSMode(mode?: CipherMode) {
  switch (mode) {
    case CipherMode.ECB:
      return CryptoJS.mode.ECB;
    default:
      return undefined;
  }
}

/**
 * Maps the padding enum to the corresponding CryptoJS padding.
 *
 * @param padding The padding to map
 */
function getCryptoJSPadding(padding?: CipherPadding) {
  switch (padding) {
    case CipherPadding.ZeroPadding:
      return CryptoJS.pad.ZeroPadding;
    default:
      return undefined;
  }
}

/**
 * Maps the algorithm enum to the corresponding CryptoJS cipher helper.
 *
 * @param algorithm The algorithm to map
 */
function getCipherHelper(algorithm: CipherAlgorithm): CipherHelper {
  switch (algorithm) {
    case CipherAlgorithm.AES:
      return CryptoJS.AES;
    default:
      throw new Error('Unknown cipher algorithm');
  }
}

export class Cipher {
  private cipherHelper: CipherHelper;
  private defaultOptions: Partial<CryptoJS.lib.CipherParams>;
  private secret: EncryptionKey;

  /**
   * @param options The options used to create a new instance
   */
  constructor(options: CreateOptions) {
    const { algorithm, defaultOptions } = options;
    this.cipherHelper = getCipherHelper(algorithm);
    this.secret = defaultOptions.secret;
    this.defaultOptions = this.parseOptions(defaultOptions);
  }

  /**
   * @param message The message to encrypt
   * @param options Can be provided to overwrite default options
   *
   * @returns The encrypted message
   */
  encrypt(message: string, options?: Partial<CipherOptions>): string {
    const secret = this.getSecret();
    const fullOptions = this.getCipherOptions(options);
    const encrypted = this.cipherHelper.encrypt(message, secret, fullOptions);

    return encrypted.toString();
  }

  /**
   * @param message The message to decrypt
   * @param options Can be provided to overwrite
   */
  decrypt(message: string, options?: Partial<CipherOptions>): string {
    const secret = this.getSecret();
    const fullOptions = this.getCipherOptions(options);
    const decrypted = this.cipherHelper.decrypt(message, secret, fullOptions);

    return decrypted.toString(CryptoJS.enc.Utf8);
  }

  /**
   * @returns
   * The secret from options if any is passed, otherwise the default secret is used.
   */
  private getSecret(options: Partial<CipherOptions> = {}): EncryptionKey {
    const { secret } = options;
    return secret !== undefined ? secret : this.secret;
  }

  /**
   * @param options The options to merge with the default options
   *
   * @returns
   * A merged version between the default options and the passed options
   */
  private getCipherOptions(
    options: Partial<CipherOptions> = {},
  ): Partial<CryptoJS.lib.CipherParams> {
    return {
      ...this.defaultOptions,
      ...this.parseOptions(options),
    };
  }

  /**
   * Maps the cipher options to CryptoJS cipher options.
   *
   * @param options The options to map
   */
  private parseOptions(
    options: Partial<CipherOptions>,
  ): Partial<CryptoJS.lib.CipherParams> {
    const { mode, padding } = options;
    const valueMap: [keyof CryptoJS.lib.CipherParams, any][] = [
      ['mode', getCryptoJSMode(mode)],
      ['padding', getCryptoJSPadding(padding)],
    ];

    return valueMap.reduce(
      (result, [propName, value]) => {
        if (value !== undefined) {
          result[propName] = value;
        }
        return result;
      },
      {} as Record<string, unknown>,
    );
  }
}

@Injectable()
export class EncryptionService {
  constructor(private encodingService: EncodingService) {}
  /**
   * @param options The options for the cipher instance
   *
   * @returns A new cipher instance
   */
  createCipher(options: CreateOptions): Cipher {
    return new Cipher(options);
  }

  /**
   * Creates a key from the given secret and encoding.
   *
   * @param secret The secret passphrase
   * @param encoding The encoding for the key
   */
  createKey(secret: string, encoding: Encoding): EncryptionKey {
    return this.encodingService.asBinary(secret, encoding);
  }
}
